/// <reference path="../url-imports.d.ts" />

import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

export interface PdfViewportProxy {
  width: number;
  height: number;
}

export interface PdfRenderTask {
  promise: Promise<unknown>;
  cancel(): void;
}

export interface PdfPageProxy {
  getViewport(params: {
    scale: number;
    rotation: number;
  }): PdfViewportProxy;
  render(params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewportProxy;
  }): PdfRenderTask;
  cleanup?(): void;
}

export interface PdfDocumentProxy {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPageProxy>;
  destroy?(): Promise<void>;
}

interface PdfJsRuntime {
  GlobalWorkerOptions: {
    workerSrc: string;
    verbosity?: number;
  };
  VerbosityLevel?: {
    ERRORS: number;
  };
  getDocument(source: {
    url: string;
    disableFontFace?: boolean;
    useSystemFonts?: boolean;
  }): {
    promise: Promise<PdfDocumentProxy>;
  };
}

interface PdfJsWorkerRuntime {
  WorkerMessageHandler: unknown;
}

type PdfJsWorkerGlobal = typeof globalThis & {
  pdfjsWorker?: PdfJsWorkerRuntime;
};

let configuredRuntime: PdfJsRuntime | null = null;

const ensurePromiseTry = () => {
  const promiseConstructor = Promise as PromiseConstructor & {
    try?: (
      callback: (...args: unknown[]) => unknown,
      ...args: unknown[]
    ) => Promise<unknown>;
  };
  if (promiseConstructor.try) return;
  promiseConstructor.try = (callback, ...args) =>
    new Promise((resolve, reject) => {
      try {
        resolve(callback(...args));
      } catch (error) {
        reject(error);
      }
    });
};

const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return (
    /Safari/i.test(navigator.userAgent) &&
    !/(Chrome|Chromium|CriOS|Edg|OPR|FxiOS|Firefox|Android)/i.test(
      navigator.userAgent,
    )
  );
};

export const loadPdfJsRuntime = async (): Promise<PdfJsRuntime> => {
  if (configuredRuntime) return configuredRuntime;
  ensurePromiseTry();

  // PDF.js 5 no longer reads the old `disableWorker` getDocument option.
  // Loading WorkerMessageHandler on the main thread is its supported fake-
  // worker signal. Do this before the first document is created on Safari so
  // PDF.js never needs its cross-origin blob-worker wrapper there.
  if (isSafari()) {
    const workerModule =
      (await import('pdfjs-dist/legacy/build/pdf.worker.mjs')) as unknown as PdfJsWorkerRuntime;
    (globalThis as PdfJsWorkerGlobal).pdfjsWorker = {
      WorkerMessageHandler: workerModule.WorkerMessageHandler,
    };
  }

  const module = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const runtime = module as unknown as PdfJsRuntime;
  runtime.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  if (runtime.VerbosityLevel) {
    runtime.GlobalWorkerOptions.verbosity =
      runtime.VerbosityLevel.ERRORS;
  }
  configuredRuntime = runtime;
  return runtime;
};

export const loadPdfDocument = async (
  fileUrl: string,
): Promise<PdfDocumentProxy> => {
  const runtime = await loadPdfJsRuntime();
  return runtime.getDocument({
    url: fileUrl,
    disableFontFace: false,
    useSystemFonts: false,
  }).promise;
};
